/**
 * Paginated fetch for Supabase queries that can exceed the row cap.
 *
 * WHY THIS EXISTS: PostgREST applies a server-side `max-rows` limit (1000 on
 * Supabase by default) to EVERY request. A query with no `.limit()` is not
 * unbounded — it is silently truncated at 1000 with no error and no flag on
 * the response. Moderation hit this twice: contacts (no `.limit()`, so it
 * looked unbounded but was not) and messages (an explicit `.limit(1000)`).
 *
 * The truncation is worse than it sounds here, because the message rows build
 * the last-message preview, flag counts and unread badges for every contact.
 * Past the cap, older conversations do not merely go missing — they render
 * with no preview and no flags, which reads as "nothing to moderate" rather
 * than "not loaded".
 *
 * Pages by `.range()` until a short page comes back, which is the only
 * reliable end-of-data signal: a full page means there may be more, a partial
 * page means there is not.
 */

/** Matches Supabase's default `max-rows`. Larger pages are silently clamped. */
const PAGE_SIZE = 1000

/**
 * A hard ceiling so a runaway table cannot hang the browser or exhaust memory.
 * Reaching it is reported through `truncated` rather than thrown: a partial
 * list is still useful, but the caller must be able to say so.
 */
const DEFAULT_MAX_ROWS = 50_000

export interface FetchAllResult<T> {
  rows: T[]
  /** True when maxRows was hit and more rows exist upstream. */
  truncated: boolean
}

/**
 * Runs `makePage(from, to)` repeatedly, accumulating rows until exhausted.
 *
 * The callback receives an inclusive range and must apply it via `.range()`,
 * returning the usual Supabase `{ data, error }`. Building the query inside
 * the callback (rather than passing one in) matters: a PostgREST query object
 * is single-use, so it has to be rebuilt for each page.
 */
export async function fetchAllRows<T>(
  makePage: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: unknown }>,
  opts: { maxRows?: number } = {},
): Promise<FetchAllResult<T>> {
  const maxRows = opts.maxRows ?? DEFAULT_MAX_ROWS
  const rows: T[] = []

  for (let from = 0; from < maxRows; from += PAGE_SIZE) {
    const to = Math.min(from + PAGE_SIZE, maxRows) - 1
    const { data, error } = await makePage(from, to)
    if (error) throw error

    const page = data ?? []
    rows.push(...page)

    // A short page means the table is exhausted. A full one is ambiguous, so
    // keep going — the next request settles it.
    if (page.length < to - from + 1) return { rows, truncated: false }
  }

  return { rows, truncated: true }
}
